import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Alert,
  Card,
  CardContent,
  TextField,
  Button,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import VendorPaymentsHeader from './VendorPaymentsHeader';
import { vendorPaymentsAPI } from '../../services/api';

const TABS = [
  { key: 'payments', label: 'Payments' },
  { key: 'vendors', label: 'Vendors' },
];

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const emptyVendorForm = { vendor_name: '' };
const emptyPaymentForm = { vendor_id: '', payment_date: '', amount_paid: '' };

function formatDate(value) {
  if (!value) return '';
  const d = new Date(`${value}T00:00:00`);
  return d.toLocaleDateString();
}

function formatAmount(value) {
  return Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
}

// payment_date comes back as 'YYYY-MM-DD' - parsed as local, not UTC, so a
// payment dated the 1st of the month never shifts to the last day of the
// previous month depending on timezone.
function paymentMonthYear(value) {
  const [y, m] = value.split('-').map(Number);
  return { month: m, year: y };
}

function VendorPaymentsPage() {
  const [activeTabIndex, setActiveTabIndex] = useState(0);
  const activeTab = TABS[activeTabIndex]?.key;

  const [vendors, setVendors] = useState([]);
  const [payments, setPayments] = useState([]);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  // Payments tab filter - defaults to the current month
  const now = new Date();
  const [paymentFilterMonth, setPaymentFilterMonth] = useState(now.getMonth() + 1);
  const [paymentFilterYear, setPaymentFilterYear] = useState(now.getFullYear());
  const filteredPayments = payments.filter((p) => {
    const { month, year } = paymentMonthYear(p.payment_date);
    return month === paymentFilterMonth && year === paymentFilterYear;
  });

  // Vendor dialog state
  const [vendorOpen, setVendorOpen] = useState(false);
  const [vendorEditing, setVendorEditing] = useState(null);
  const [vendorForm, setVendorForm] = useState(emptyVendorForm);

  // Payment dialog state
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [paymentEditing, setPaymentEditing] = useState(null);
  const [paymentForm, setPaymentForm] = useState(emptyPaymentForm);

  const loadAll = async () => {
    try {
      const [vendorsRes, paymentsRes] = await Promise.all([
        vendorPaymentsAPI.getVendors(),
        vendorPaymentsAPI.getPayments(),
      ]);
      setVendors(vendorsRes.data);
      setPayments(paymentsRes.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load');
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const flash = (msg) => {
    setMessage(msg);
    setTimeout(() => setMessage(''), 3000);
  };

  // ==================== VENDORS ====================
  const openAddVendor = () => {
    setVendorEditing(null);
    setVendorForm(emptyVendorForm);
    setError('');
    setVendorOpen(true);
  };

  const openEditVendor = (vendor) => {
    setVendorEditing(vendor);
    setVendorForm({ vendor_name: vendor.vendor_name });
    setError('');
    setVendorOpen(true);
  };

  const closeVendor = () => {
    setVendorOpen(false);
    setVendorForm(emptyVendorForm);
  };

  const handleSaveVendor = async () => {
    if (!vendorForm.vendor_name.trim()) {
      setError('Vendor Name is required');
      return;
    }
    try {
      if (vendorEditing) {
        await vendorPaymentsAPI.updateVendor(vendorEditing.id, vendorForm);
        flash('Vendor updated');
      } else {
        await vendorPaymentsAPI.createVendor(vendorForm);
        flash('Vendor added');
      }
      closeVendor();
      loadAll();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save Vendor');
    }
  };

  const handleDeleteVendor = async (vendor) => {
    if (!window.confirm(`Delete vendor "${vendor.vendor_name}"? Past payments to them stay on record.`)) return;
    try {
      await vendorPaymentsAPI.deleteVendor(vendor.id);
      flash('Vendor deleted');
      loadAll();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete Vendor');
    }
  };

  // ==================== PAYMENTS ====================
  const openAddPayment = () => {
    setPaymentEditing(null);
    setPaymentForm(emptyPaymentForm);
    setError('');
    setPaymentOpen(true);
  };

  const openEditPayment = (payment) => {
    setPaymentEditing(payment);
    setPaymentForm({
      vendor_id: payment.vendor_id,
      payment_date: payment.payment_date,
      amount_paid: payment.amount_paid,
    });
    setError('');
    setPaymentOpen(true);
  };

  const closePayment = () => {
    setPaymentOpen(false);
    setPaymentForm(emptyPaymentForm);
  };

  const handleSavePayment = async () => {
    if (!paymentForm.vendor_id || !paymentForm.payment_date || !paymentForm.amount_paid) {
      setError('Vendor, Date, and Amount Paid are all required');
      return;
    }
    try {
      const payload = {
        vendor_id: paymentForm.vendor_id,
        payment_date: paymentForm.payment_date,
        amount_paid: parseFloat(paymentForm.amount_paid),
      };
      if (paymentEditing) {
        await vendorPaymentsAPI.updatePayment(paymentEditing.id, payload);
        flash('Payment updated');
      } else {
        await vendorPaymentsAPI.createPayment(payload);
        flash('Payment added');
      }
      closePayment();
      loadAll();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save Payment');
    }
  };

  const handleDeletePayment = async (payment) => {
    if (!window.confirm(`Delete this payment to ${payment.vendor_name}?`)) return;
    try {
      await vendorPaymentsAPI.deletePayment(payment.id);
      flash('Payment deleted');
      loadAll();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete Payment');
    }
  };

  return (
    <Box>
      <VendorPaymentsHeader />
      <Container maxWidth="md" sx={{ mt: 3, mb: 4 }}>
        <Tabs
          value={activeTabIndex}
          onChange={(e, newValue) => setActiveTabIndex(newValue)}
          sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
        >
          {TABS.map((tab) => (
            <Tab key={tab.key} label={tab.label} />
          ))}
        </Tabs>

        {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}
        {error && !vendorOpen && !paymentOpen && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {activeTab === 'vendors' && (
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6">Vendors</Typography>
                <Button variant="contained" startIcon={<Add />} onClick={openAddVendor}>Add</Button>
              </Box>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Vendor Name</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {vendors.length === 0 ? (
                      <TableRow><TableCell colSpan={2} align="center" sx={{ color: 'text.secondary' }}>No Vendors yet</TableCell></TableRow>
                    ) : vendors.map((v) => (
                      <TableRow key={v.id} hover>
                        <TableCell>{v.vendor_name}</TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => openEditVendor(v)}><Edit fontSize="small" /></IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDeleteVendor(v)}><Delete fontSize="small" /></IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}

        {activeTab === 'payments' && (
          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1 }}>Payments</Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <FormControl size="small" sx={{ minWidth: 130 }}>
                    <InputLabel>Month</InputLabel>
                    <Select label="Month" value={paymentFilterMonth} onChange={(e) => setPaymentFilterMonth(e.target.value)}>
                      {MONTH_NAMES.map((m, idx) => (
                        <MenuItem key={m} value={idx + 1}>{m}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 100 }}>
                    <InputLabel>Year</InputLabel>
                    <Select label="Year" value={paymentFilterYear} onChange={(e) => setPaymentFilterYear(e.target.value)}>
                      {[now.getFullYear(), now.getFullYear() - 1].map((y) => (
                        <MenuItem key={y} value={y}>{y}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <Button variant="contained" startIcon={<Add />} onClick={openAddPayment} disabled={vendors.length === 0}>Add</Button>
              </Box>
              {vendors.length === 0 && (
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  Add a Vendor first before recording a payment.
                </Typography>
              )}
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Vendor</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell align="right">Amount Paid</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredPayments.length === 0 ? (
                      <TableRow><TableCell colSpan={4} align="center" sx={{ color: 'text.secondary' }}>No Payments for {MONTH_NAMES[paymentFilterMonth - 1]} {paymentFilterYear}</TableCell></TableRow>
                    ) : filteredPayments.map((p) => (
                      <TableRow key={p.id} hover>
                        <TableCell>{p.vendor_name}</TableCell>
                        <TableCell>{formatDate(p.payment_date)}</TableCell>
                        <TableCell align="right">₹{formatAmount(p.amount_paid)}</TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => openEditPayment(p)}><Edit fontSize="small" /></IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDeletePayment(p)}><Delete fontSize="small" /></IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}
      </Container>

      {/* Vendor dialog */}
      <Dialog open={vendorOpen} onClose={closeVendor} maxWidth="xs" fullWidth>
        <DialogTitle>{vendorEditing ? 'Edit Vendor' : 'Add Vendor'}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
          <TextField
            fullWidth
            label="Vendor Name"
            value={vendorForm.vendor_name}
            onChange={(e) => setVendorForm({ ...vendorForm, vendor_name: e.target.value })}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeVendor}>Cancel</Button>
          <Button onClick={handleSaveVendor} variant="contained">{vendorEditing ? 'Update' : 'Add'}</Button>
        </DialogActions>
      </Dialog>

      {/* Payment dialog */}
      <Dialog open={paymentOpen} onClose={closePayment} maxWidth="xs" fullWidth>
        <DialogTitle>{paymentEditing ? 'Edit Payment' : 'Add Payment'}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
          <FormControl fullWidth sx={{ mt: 1, mb: 2 }}>
            <InputLabel>Vendor</InputLabel>
            <Select
              label="Vendor"
              value={paymentForm.vendor_id}
              onChange={(e) => setPaymentForm({ ...paymentForm, vendor_id: e.target.value })}
            >
              {vendors.map((v) => (
                <MenuItem key={v.id} value={v.id}>{v.vendor_name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            type="date"
            label="Date"
            value={paymentForm.payment_date}
            onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            type="number"
            label="Amount Paid"
            value={paymentForm.amount_paid}
            onChange={(e) => setPaymentForm({ ...paymentForm, amount_paid: e.target.value })}
            inputProps={{ min: 0 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closePayment}>Cancel</Button>
          <Button onClick={handleSavePayment} variant="contained">{paymentEditing ? 'Update' : 'Add'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default VendorPaymentsPage;
